import BaseFormatter from './base'
import {
  SignalVerdict,
  AllExtractedFilesTypes,
  ExtractedFile,
  SignalGroup,
  IOCDataTypes,
  IOCGroup,
  AllFilesTypes,
} from '../../types'
import {
  DynamicStageInfo,
  ReputationStageInfo,
  ScanStagesInfo,
  ScanStageType,
  StaticStageInfo,
  TotalAndMaliciousCount,
} from '../../features/overview-report/types'
import { productFeaturesObject, ProductFeature } from 'app/modules/shared'
import { getOrderedThreatVerdicts, getThreatVerdicts } from '../../helpers/signal-type'

/**
 * Format report response data for overview page
 */
export default class OverviewFormatter extends BaseFormatter {
  static staticGroupsCount: number = 0

  /**
   * Format original report data
   *
   * @param data
   * @param report
   */
  format(data: { [key: string]: any }, report: { [key: string]: any }): void {
    const result: { [key: string]: any } = {}
    const resource = this.getResource(data, 'file')

    result.private = resource?.private ?? false
    result.fileSize = resource?.fileSize
    result.mediaTypeDescription = resource?.extendedData?.fileMagicDescription

    if (resource?.mediaType?.string) {
      result.mediaType = resource.mediaType.string
    }
    if (data.allTags) {
      result.tags = data.allTags
    }
    if (data.created_date) {
      result.date = data.created_date
    }
    if (data.estimated_progress) {
      result.estimatedProgress = data.estimated_progress
    }
    if (data.latestReport) {
      result.latestReport = data.latestReport
    }

    const meta = resource?.metaData
    if (resource?.metaData?.isUrlToFileAnalysis) {
      const urlScanInfo: { [key: string]: any } = { isUrlToFileAnalysis: true }

      if (resource.digests && resource.digests['SHA-256']) {
        urlScanInfo.fileIdentifier = resource.digests['SHA-256']
      }

      for (const name in ['originalUrl', 'originalUrlIdentifier']) {
        if (meta[name]) {
          urlScanInfo[name] = meta[name]
        }
      }

      result.urlScanInfo = urlScanInfo
    }

    for (const name of ['entropy']) {
      if (meta?.[name]) {
        result[name] = meta[name]
      }
    }

    result.scanStages = this.gatherScanStagesInfo(report)
    result.files = this.getFilesOverview(report)
    result.stringsCount = this.getStringsCount(report)
    report.overview = result
  }

  // Get stat on total amount of various extracted/downloaded files
  getFilesOverview(report: { [key: string]: any }) {
    const files = report.extracted_files?.files
    if (!files) {
      return null
    }

    const result = {
      [AllExtractedFilesTypes.ContextFiles]: { total: 0, malicious: 0 },
      [AllExtractedFilesTypes.ExtractedFiles]: { total: 0, malicious: 0 },
      [AllExtractedFilesTypes.DownloadedFiles]: { total: 0, malicious: 0 },
    }

    for (const type in files) {
      result[type as keyof typeof result].total = files[type].length
      result[type as keyof typeof result].malicious = files[type].reduce((count: number, file: ExtractedFile) => {
        const isMalicious = [SignalVerdict.Malicious, SignalVerdict.Likely_malicious].includes(
          file.verdict?.verdict as SignalVerdict
        )
        return count + (isMalicious ? 1 : 0)
      }, 0)
    }

    return result
  }

  // Gather all info needed for ScanStages section
  gatherScanStagesInfo(report: { [key: string]: any }) {
    if (productFeaturesObject.hasFeature(ProductFeature.Aether)) {
      return null
    }

    const reputationKey = ScanStageType.Reputation_service
    const staticKey = ScanStageType.Deep_static_analysis
    const dynamicKey = ScanStageType.Dynamic_fast_pass
    const threatIndicatorsCount = this.countThreatIndicatorsByStage(report.threatIndicators?.groups || {})
    const iocsCount = this.countIOCsByStage(report.ioc?.data?.mainFile || {})
    const result: ScanStagesInfo = {
      [staticKey]: this.gatherStaticStageInfo(report, threatIndicatorsCount, iocsCount),
      [dynamicKey]: this.gatherDynamicStageInfo(report, threatIndicatorsCount, iocsCount),
      [reputationKey]: this.gatherReputationStageInfo(report, threatIndicatorsCount),
    }

    return result
  }

  // Gather all info for Static stage
  gatherStaticStageInfo(
    report: { [key: string]: any },
    threatIndicatorsCount: { [key in ScanStageType]: TotalAndMaliciousCount },
    iocsCount: { [key in ScanStageType]?: TotalAndMaliciousCount }
  ) {
    const result: StaticStageInfo = {
      duration: 0, // to be implemented on transform
      obfuscationTechniquesCount: OverviewFormatter.staticGroupsCount,
      threatIndicatorsCount: threatIndicatorsCount[ScanStageType.Deep_static_analysis],
      iocsCount: iocsCount[ScanStageType.Deep_static_analysis],
    }

    return result
  }

  // Gather all info for Dynamic stage
  gatherDynamicStageInfo(
    report: { [key: string]: any },
    threatIndicatorsCount: { [key in ScanStageType]: TotalAndMaliciousCount },
    iocsCount: { [key in ScanStageType]?: TotalAndMaliciousCount }
  ) {
    const result: DynamicStageInfo = {
      duration: 0, // to be implemented on transform
      actionsCount: report.emulation_data?.data?.input?.data?.length || 0,
      threatIndicatorsCount: threatIndicatorsCount[ScanStageType.Dynamic_fast_pass],
      iocsCount: iocsCount[ScanStageType.Dynamic_fast_pass],
    }

    return result
  }

  // Count IOCs by stage, total and malicious
  countIOCsByStage(iocs: { [key in IOCDataTypes]: IOCGroup[] }) {
    const result = {
      [ScanStageType.Deep_static_analysis]: {
        total: 0,
        malicious: 0,
      },
      [ScanStageType.Dynamic_fast_pass]: {
        total: 0,
        malicious: 0,
      },
    }

    for (const iocType in iocs) {
      const groups = iocs[iocType as IOCDataTypes]
      for (const group of groups) {
        for (const ioc of group.references) {
          const isDynamic = ioc.origins?.join(' ').indexOf('emulation') !== -1
          const isMalicious = [SignalVerdict.Malicious, SignalVerdict.Likely_malicious].includes(
            ioc.verdict as SignalVerdict
          )
          const key = isDynamic ? ScanStageType.Dynamic_fast_pass : ScanStageType.Deep_static_analysis
          result[key].total += 1

          if (isMalicious) {
            result[key].malicious += 1
          }
        }
      }
    }

    return result
  }

  // Gather all info for Reputation stage
  gatherReputationStageInfo(
    report: { [key: string]: any },
    threatIndicatorsCount: { [key in ScanStageType]: TotalAndMaliciousCount }
  ) {
    const result: ReputationStageInfo = { isEnabled: false }
    const subtasks: any[] = report.progress?.subtasks || []
    const osint = report.osint
    result.isEnabled = !!osint?.overview?.providers?.includes('OpswatReputation')

    if (!result.isEnabled) {
      return result
    }

    result.duration = subtasks.reduce((sum: number, task: { [key: string]: any }) => {
      const name = task.name?.toLowerCase()
      if (name && (name.indexOf('osint') !== -1 || name.indexOf('fuzzy hash') !== -1)) {
        sum += (task.duration?.minutes || 0) * 60 + (task.duration.seconds || 0)
      }

      return sum
    }, 0)

    result.checksCount = Object.keys(osint.groups || {}).reduce((sum: number, verdict: string) => {
      const iocsData = osint.groups[verdict]

      for (const iocType in iocsData) {
        const iocs = iocsData[iocType]
        for (const ioc of iocs) {
          sum += Object.keys(ioc.result?.data?.scan_results?.scan_details || {}).length
        }
      }

      return sum
    }, 0)

    const statVerdicts = osint.filter?.stat?.types?.verdict || {}
    result.iocsCount = {
      total: osint.filter?.stat?.total || 0,
      malicious: (statVerdicts[SignalVerdict.Malicious] || 0) + (statVerdicts[SignalVerdict.Likely_malicious] || 0),
    }
    result.threatIndicatorsCount = threatIndicatorsCount[ScanStageType.Reputation_service]

    return result
  }

  // Count Threat Indicators by stage
  countThreatIndicatorsByStage(threatIndicators: { [verdictKey in SignalVerdict]?: SignalGroup[] }) {
    const result = {
      [ScanStageType.Reputation_service]: { total: 0, malicious: 0 },
      [ScanStageType.Deep_static_analysis]: { total: 0, malicious: 0 },
      [ScanStageType.Dynamic_fast_pass]: { total: 0, malicious: 0 },
    }

    Object.values(threatIndicators).forEach((groups: SignalGroup[] | undefined) => {
      if (!groups) {
        return
      }

      groups.forEach((group: SignalGroup) => {
        const isMalicious = [SignalVerdict.Malicious, SignalVerdict.Likely_malicious].includes(
          group.verdict as SignalVerdict
        )

        result[group.type].total += 1
        result[group.type].malicious += isMalicious ? 1 : 0
      })
    })

    return result
  }

  getStringsCount(report: { [key: string]: any }): number {
    const filter = report.strings?.filter.files
    let count = filter?.[AllFilesTypes.MainFile]?.stat?.total || 0

    for (const fileType of [AllFilesTypes.ExtractedFiles, AllFilesTypes.DownloadedFiles]) {
      for (const fileHash in filter?.[fileType] || {}) {
        const file = filter[fileType][fileHash]
        count += file?.stat?.total || 0
      }
    }

    return count
  }
}
